import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  Alert,
  CircularProgress,
  Collapse,
  IconButton,
  Tooltip,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { Link, useNavigate } from 'react-router-dom';
import { useAxiosClient } from '../utils/useAxiosClient';
import { useDevAuth } from '../utils/useDevAuth';

interface RunLogRow {
  id: number;
  runId: string;
  prefectFlowRunId?: string | null;
  collector: string;
  level: 'run' | 'org' | 'account';
  organizationId?: number | null;
  organizationName?: string | null;
  platform?: string | null;
  accountName?: string | null;
  brandname?: string | null;
  status: string;
  errorType?: string | null;
  message?: string | null;
  details?: { account_counts?: Record<string, number>; org_count?: number; traceback?: string } | null;
  numberOfPosts?: number | null;
  startedAt?: string | null;
  completedAt?: string | null;
}

interface FailingAccount {
  alertId: number;
  organizationId: number;
  organizationName?: string | null;
  platform: string;
  accountName: string;
  message?: string | null;
  consecutiveFailures?: number | null;
  firstFailedAt?: string | null;
  lastFailedAt?: string | null;
  collector?: string | null;
}

interface RunDetail {
  orgs: RunLogRow[];
  accounts: RunLogRow[];
}

interface OpenRequest {
  id: number;
  organizationId: number;
  organizationName?: string | null;
  collector: string;
  status: string;
  requestedBy?: string | null;
  requestedAt?: string | null;
  heartbeatAt?: string | null;
  notBefore?: string | null;
  attempts?: number | null;
  message?: string | null;
}

interface YoutubeQuotaDay {
  quotaDate: string;
  units: number;
  oneoffUnits: number;
  quotaExceededAt?: string | null;
  unitsAtExceeded?: number | null;
}

interface PrefectRun {
  flowRunId: string;
  name: string;
  label: string;
  isNightly: boolean;
  isOneoff: boolean;
  organizationId?: number | null;
  organizationName?: string | null;
  state: string;
  stateName?: string | null;
  expectedStartTime?: string | null;
  startTime?: string | null;
  endTime?: string | null;
}

interface PrefectActivity {
  machine: { online: boolean; lastTickAt?: string | null; prefectOk?: boolean | null; message?: string | null };
  running: PrefectRun[];
  upcoming: PrefectRun[];
  recent: PrefectRun[];
}

const PREFECT_STATE_COLORS: Record<string, 'success' | 'error' | 'warning' | 'info' | 'default'> = {
  RUNNING: 'info',
  PENDING: 'info',
  SCHEDULED: 'default',
  COMPLETED: 'success',
  FAILED: 'error',
  CRASHED: 'error',
  CANCELLED: 'warning',
  CANCELLING: 'warning',
};

const YT_DAILY_LIMIT = 10000;

const STATUS_COLORS: Record<string, 'success' | 'error' | 'warning' | 'info' | 'default'> = {
  success: 'success',
  up_to_date: 'success',
  partial: 'warning',
  skipped: 'default',
  interrupted: 'warning',
  failed: 'error',
  running: 'info',
  pending: 'info',
  stop_requested: 'warning',
  paused: 'warning',
  awaiting_ready: 'warning',
};

const StatusChip: React.FC<{ status: string }> = ({ status }) => (
  <Chip size="small" label={status.replace('_', ' ')} color={STATUS_COLORS[status] || 'default'} />
);

const formatDateTime = (iso?: string | null) => (iso ? new Date(iso).toLocaleString() : '-');

const formatDuration = (start?: string | null, end?: string | null) => {
  if (!start || !end) return '-';
  const minutes = Math.round((new Date(end).getTime() - new Date(start).getTime()) / 60000);
  if (minutes < 60) return `${minutes}m`;
  return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
};

const formatCounts = (counts?: Record<string, number>) =>
  counts
    ? Object.entries(counts)
        .map(([status, count]) => `${count} ${status.replace('_', ' ')}`)
        .join(', ')
    : '-';

/**
 * Admin API Status - nightly collector runs and the accounts currently failing and being skipped.
 * Reads collection_run_log (written by the collectors) via /api/admin/api-status.
 */
const AdminApiStatus: React.FC = () => {
  const navigate = useNavigate();
  const axiosClient = useAxiosClient();
  const { user } = useDevAuth();

  const [runs, setRuns] = useState<RunLogRow[]>([]);
  const [failingAccounts, setFailingAccounts] = useState<FailingAccount[]>([]);
  const [openRequests, setOpenRequests] = useState<OpenRequest[]>([]);
  const [youtubeQuota, setYoutubeQuota] = useState<YoutubeQuotaDay[]>([]);
  const [prefect, setPrefect] = useState<PrefectActivity | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedRunId, setExpandedRunId] = useState<string | null>(null);
  const [runDetails, setRunDetails] = useState<Record<string, RunDetail>>({});

  // Check if user is admin
  const provider = user?.sub?.split('|')[0];
  const emailLower = user?.email?.toLowerCase() || '';
  const isAdmin = (provider === 'google-oauth2' || provider === 'clerk') && (emailLower === 'weezilla@gmail.com' || emailLower.endsWith('@crant.ai'));

  const fetchStatus = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axiosClient.get('/api/admin/api-status');
      setRuns(response.data.runs || []);
      setFailingAccounts(response.data.failingAccounts || []);
      setOpenRequests(response.data.openRequests || []);
      setYoutubeQuota(response.data.youtubeQuota || []);
      setPrefect(response.data.prefect || null);
    } catch (err) {
      console.error('Error fetching API status:', err);
      setError('Could not load API status.');
    } finally {
      setLoading(false);
    }
  }, [axiosClient]);

  useEffect(() => {
    if (!isAdmin) return;
    fetchStatus();
  }, [isAdmin, fetchStatus]);

  const toggleRun = async (runId: string) => {
    if (expandedRunId === runId) {
      setExpandedRunId(null);
      return;
    }
    setExpandedRunId(runId);
    if (!runDetails[runId]) {
      try {
        const response = await axiosClient.get(`/api/admin/api-status/runs/${runId}`);
        setRunDetails((prev) => ({ ...prev, [runId]: response.data }));
      } catch (err) {
        console.error('Error fetching run detail:', err);
      }
    }
  };

  if (!isAdmin) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4">Unauthorized</Typography>
        <Typography>You do not have access to this page.</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1400, margin: '0 auto' }}>
      <Button variant="text" onClick={() => navigate('/admin')} sx={{ mb: 2 }}>
        ← Back to Admin Dashboard
      </Button>

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 600 }}>
          API Status
        </Typography>
        <Button variant="outlined" onClick={fetchStatus} disabled={loading}>
          Refresh
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Typography variant="h6" sx={{ mb: 1 }}>
        Collection machine (Prefect)
      </Typography>
      {prefect && !prefect.machine.online && (
        <Alert severity="error" sx={{ mb: 2 }}>
          The collection machine is not reporting
          {prefect.machine.lastTickAt ? ` (last seen ${formatDateTime(prefect.machine.lastTickAt)})` : ''}. Nothing is being
          collected and one-off requests stay queued. {prefect.machine.message || ''}
        </Alert>
      )}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Published every 2 minutes by the machine that runs the collectors
        {prefect?.machine.lastTickAt ? `; last update ${formatDateTime(prefect.machine.lastTickAt)}` : ''}. A nightly flow that
        shows Completed may still have skipped accounts: see the failing accounts and run details below.
      </Typography>
      <TableContainer component={Paper} sx={{ mb: 4 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Job</TableCell>
              <TableCell>Organization</TableCell>
              <TableCell>State</TableCell>
              <TableCell>Started / scheduled</TableCell>
              <TableCell>Duration</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!prefect || prefect.running.length + prefect.upcoming.length + prefect.recent.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ color: 'text.secondary' }}>
                  {loading ? 'Loading...' : 'No Prefect activity published yet.'}
                </TableCell>
              </TableRow>
            ) : (
              [...prefect.running, ...prefect.upcoming.slice(0, 6), ...prefect.recent.slice(0, 12)].map((run) => (
                <TableRow key={run.flowRunId} sx={run.state === 'RUNNING' ? { backgroundColor: 'action.hover' } : undefined}>
                  <TableCell>{run.label}</TableCell>
                  <TableCell>
                    {run.organizationId ? (
                      <Link to={`/admin/org/${run.organizationId}`} style={{ textDecoration: 'none', color: '#1976d2' }}>
                        {run.organizationName || run.organizationId}
                      </Link>
                    ) : run.isNightly ? (
                      'all collecting organizations'
                    ) : (
                      '-'
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={(run.stateName || run.state).toLowerCase()} color={PREFECT_STATE_COLORS[run.state] || 'default'} />
                  </TableCell>
                  <TableCell>{formatDateTime(run.startTime || run.expectedStartTime)}</TableCell>
                  <TableCell>{run.startTime ? formatDuration(run.startTime, run.endTime || new Date().toISOString()) : '-'}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Typography variant="h6" sx={{ mb: 1 }}>
        Accounts currently failing ({failingAccounts.length})
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        These accounts raised an error and were skipped so the rest of the run could continue. They are retried every
        run and drop off this list on the next successful collection.
      </Typography>
      <TableContainer component={Paper} sx={{ mb: 4 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Organization</TableCell>
              <TableCell>Platform</TableCell>
              <TableCell>Account</TableCell>
              <TableCell align="right">Runs failed in a row</TableCell>
              <TableCell>First failed</TableCell>
              <TableCell>Last failed</TableCell>
              <TableCell>Error</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {failingAccounts.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ color: 'text.secondary' }}>
                  {loading ? 'Loading...' : 'No failing accounts.'}
                </TableCell>
              </TableRow>
            )}
            {failingAccounts.map((account) => (
              <TableRow key={account.alertId}>
                <TableCell>
                  <Link to={`/admin/org/${account.organizationId}`} style={{ textDecoration: 'none', color: '#1976d2' }}>
                    {account.organizationName || account.organizationId}
                  </Link>
                </TableCell>
                <TableCell>{account.platform}</TableCell>
                <TableCell>{account.accountName}</TableCell>
                <TableCell align="right">{account.consecutiveFailures ?? '-'}</TableCell>
                <TableCell>{formatDateTime(account.firstFailedAt)}</TableCell>
                <TableCell>{formatDateTime(account.lastFailedAt)}</TableCell>
                <TableCell sx={{ maxWidth: 480, wordBreak: 'break-word' }}>{account.message}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Typography variant="h6" sx={{ mb: 1 }}>
        One-off collections in progress ({openRequests.length})
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Queued with "Ready for collection" on an organization page. They pause at 1:45 AM ET for the nightly run, and an
        organization that is still backfilling is collected last by the nightly.
      </Typography>
      <TableContainer component={Paper} sx={{ mb: 4 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Organization</TableCell>
              <TableCell>Collector</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Requested</TableCell>
              <TableCell>Last progress</TableCell>
              <TableCell align="right">Attempts</TableCell>
              <TableCell>Note</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {openRequests.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ color: 'text.secondary' }}>
                  {loading ? 'Loading...' : 'Nothing queued.'}
                </TableCell>
              </TableRow>
            )}
            {openRequests.map((request) => (
              <TableRow key={request.id}>
                <TableCell>
                  <Link to={`/admin/org/${request.organizationId}`} style={{ textDecoration: 'none', color: '#1976d2' }}>
                    {request.organizationName || request.organizationId}
                  </Link>
                </TableCell>
                <TableCell>{request.collector}</TableCell>
                <TableCell>
                  <StatusChip status={request.status} />
                </TableCell>
                <TableCell>{formatDateTime(request.requestedAt)}</TableCell>
                <TableCell>{formatDateTime(request.heartbeatAt)}</TableCell>
                <TableCell align="right">{request.attempts ?? 0}</TableCell>
                <TableCell sx={{ maxWidth: 420, wordBreak: 'break-word' }}>
                  {request.message || '-'}
                  {request.notBefore ? ` (resumes after ${formatDateTime(request.notBefore)})` : ''}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Typography variant="h6" sx={{ mb: 1 }}>
        YouTube quota (last 14 days)
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Units we tracked per Pacific day (the quota resets at midnight Pacific), against a daily limit of{' '}
        {YT_DAILY_LIMIT.toLocaleString()}. One-offs may use 75% of what the nightly is expected to leave.
      </Typography>
      <TableContainer component={Paper} sx={{ mb: 4 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Pacific date</TableCell>
              <TableCell align="right">Units used</TableCell>
              <TableCell align="right">Of which one-offs</TableCell>
              <TableCell align="right">% of limit</TableCell>
              <TableCell>Quota exceeded</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {youtubeQuota.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ color: 'text.secondary' }}>
                  {loading ? 'Loading...' : 'No YouTube usage tracked yet.'}
                </TableCell>
              </TableRow>
            )}
            {youtubeQuota.map((day) => (
              <TableRow key={day.quotaDate}>
                <TableCell>{day.quotaDate}</TableCell>
                <TableCell align="right">{day.units.toLocaleString()}</TableCell>
                <TableCell align="right">{day.oneoffUnits.toLocaleString()}</TableCell>
                <TableCell align="right">{Math.round((day.units / YT_DAILY_LIMIT) * 100)}%</TableCell>
                <TableCell>
                  {day.quotaExceededAt
                    ? `${formatDateTime(day.quotaExceededAt)} at ${(day.unitsAtExceeded ?? 0).toLocaleString()} tracked units`
                    : '-'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Typography variant="h6" sx={{ mb: 2 }}>
        Recent collector runs
      </Typography>
      {loading && runs.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell />
                <TableCell>Collector</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Started</TableCell>
                <TableCell>Duration</TableCell>
                <TableCell>Accounts</TableCell>
                <TableCell>Message</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {runs.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ color: 'text.secondary' }}>
                    No collector runs logged yet.
                  </TableCell>
                </TableRow>
              )}
              {runs.map((run) => {
                const isExpanded = expandedRunId === run.runId;
                const detail = runDetails[run.runId];
                return (
                  <React.Fragment key={run.id}>
                    <TableRow hover>
                      <TableCell sx={{ width: 48 }}>
                        <IconButton size="small" onClick={() => toggleRun(run.runId)}>
                          {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                        </IconButton>
                      </TableCell>
                      <TableCell>
                        <Tooltip title={run.prefectFlowRunId ? `Prefect flow run ${run.prefectFlowRunId}` : 'Not started by Prefect'}>
                          <span>{run.collector}</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <StatusChip status={run.status} />
                      </TableCell>
                      <TableCell>{formatDateTime(run.startedAt)}</TableCell>
                      <TableCell>{formatDuration(run.startedAt, run.completedAt)}</TableCell>
                      <TableCell>{formatCounts(run.details?.account_counts)}</TableCell>
                      <TableCell sx={{ maxWidth: 360, wordBreak: 'break-word' }}>{run.message || '-'}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={7} sx={{ py: 0, borderBottom: isExpanded ? undefined : 'none' }}>
                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                          <Box sx={{ m: 2 }}>
                            {!detail ? (
                              <CircularProgress size={20} />
                            ) : (
                              detail.orgs.map((org) => (
                                <Box key={org.id} sx={{ mb: 2 }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                    <Typography variant="subtitle2">{org.organizationName || org.organizationId}</Typography>
                                    <StatusChip status={org.status} />
                                    <Typography variant="caption" color="text.secondary">
                                      {formatCounts(org.details?.account_counts)}
                                    </Typography>
                                  </Box>
                                  {detail.accounts
                                    .filter((a) => a.organizationId === org.organizationId && (a.status === 'failed' || a.status === 'skipped'))
                                    .map((a) => (
                                      <Typography key={a.id} variant="body2" sx={{ ml: 2, wordBreak: 'break-word' }}>
                                        <StatusChip status={a.status} /> {a.platform}:{a.accountName}
                                        {a.message ? ` - ${a.errorType ? `${a.errorType}: ` : ''}${a.message}` : ''}
                                      </Typography>
                                    ))}
                                </Box>
                              ))
                            )}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default AdminApiStatus;
